
import { ArrowDown, ArrowRight, ArrowUp, Zap, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

export type PriorityLevel = 'low' | 'medium' | 'high' | 'urgent'

interface PriorityConfig {
    label: string
    className: string
    icon?: React.FC<{ className?: string }>
}

const PRIORITY_CONFIG: Record<PriorityLevel, PriorityConfig> = {
    low: {
        label: '低',
        className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
        icon: ArrowDown,
    },
    medium: {
        label: '中',
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        icon: ArrowRight,
    },
    high: {
        label: '高',
        className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        icon: ArrowUp,
    },
    urgent: {
        label: '紧急',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        icon: Zap,
    },
}

interface PriorityBadgeProps {
    priority: string // Accepts PriorityLevel or any string (normalized to medium)
    className?: string
    showIcon?: boolean
    compact?: boolean
    loading?: boolean
}

export function PriorityBadge({
    priority,
    className,
    showIcon = true,
    compact = false,
    loading = false,
}: PriorityBadgeProps) {
    // 规范化 priority
    const normalizedPriority = (priority?.toLowerCase() as PriorityLevel) || 'medium'
    const config = PRIORITY_CONFIG[normalizedPriority] || PRIORITY_CONFIG.medium
    const Icon = config.icon

    if (compact) {
        return (
            <span className={cn('inline-flex items-center', className)}>
                {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                    Icon && <Icon className={cn('h-4 w-4', config.className.split(' ')[1])} />
                )}
            </span>
        )
    }

    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full font-medium',
                'px-2 py-0.5 text-xs',
                config.className,
                className
            )}
        >
            {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
            ) : showIcon && Icon ? (
                <Icon className="h-3 w-3" />
            ) : null}
            {config.label}
        </span>
    )
}

export function getPriorityLabel(priority: string): string {
    const normalized = (priority?.toLowerCase() as PriorityLevel) || 'medium'
    return PRIORITY_CONFIG[normalized]?.label ?? priority
}

export function getPriorityColor(priority: string): string {
    const normalized = (priority?.toLowerCase() as PriorityLevel) || 'medium'
    return PRIORITY_CONFIG[normalized]?.className ?? ''
}
